/**
 * Dashboard Navigation Component
 *
 * Provides navigation tabs for different dashboard modules
 * Displays within the main dashboard area
 */

import React from 'react';
import {useDashboardViewStore, DashboardViewType} from '../store/dashboardViewStore';
import {useAuthorization} from '../../auth/hooks/useAuthorization';
import {AppTitle, AppText, AppSmallText} from '../../../Presentation/components/ui/Typography';
import Card from '../../../Presentation/components/ui/Card';

interface NavigationTab {
  id: DashboardViewType;
  label: string;
  icon: string;
  description: string;
  permission?: string;
}

const DashboardNavigation: React.FC = () => {
  const {currentView, setCurrentView} = useDashboardViewStore();
  const {hasPermission, user} = useAuthorization();


  const navigationTabs: NavigationTab[] = [
    {
      id: 'home',
      label: 'الرئيسية',
      icon: '🏠',
      description: 'الصفحة الرئيسية والملخص العام'
    },
    {
      id: 'map',
      label: 'الخريطة',
      icon: '🗺️',
      description: 'عرض المتاجر والمسوقين على الخريطة',
      permission: 'stores.read'
    },
    {
      id: 'analytics',
      label: 'التحليلات',
      icon: '📈',
      description: 'تحليلات مفصلة للبيانات'
    }
  ];

  const handleTabChange = (tabId: DashboardViewType) => {
    setCurrentView(tabId);
  };

  const visibleTabs = navigationTabs.filter(tab => {
    if (!tab.permission) {return true;}

    // If user is admin, show all tabs
    const isUserAdmin = user?.isAdmin && user.isAdmin();
    const isAdminByRole = user?.role?.name === 'admin' || user?.role?.name === 'super_admin';
    const hasAdminPermissions = user?.permissions?.some(p => p.includes('admin') || p.includes('manage') || p.includes('all'));

    if (isUserAdmin || isAdminByRole || hasAdminPermissions) {
      return true;
    }

    const hasAccess = hasPermission(tab.permission);
    return hasAccess;
  });

  return (
    <Card className="p-1 mb-6">
      <nav className="flex space-x-1 space-x-reverse">
        {visibleTabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => handleTabChange(tab.id)}
            className={`flex-1 flex flex-col items-center py-4 px-3 rounded-lg transition-all duration-200 ${
              currentView === tab.id
                ? 'bg-brand-50 text-brand-700 shadow-sm'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
            }`}
          >
            <div className="text-2xl mb-2">{tab.icon}</div>
            <AppTitle className={`text-sm font-medium ${currentView === tab.id ? 'text-brand-700' : 'text-gray-700'}`}>
              {tab.label}
            </AppTitle>
            <AppSmallText className={`mt-1 ${currentView === tab.id ? 'text-brand-600' : 'text-gray-500'}`}>
              {tab.description}
            </AppSmallText>
          </button>
        ))}
      </nav>
    </Card>
  );
};

export default DashboardNavigation;
